import React, { type PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import noop from 'lodash/noop';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { ExternalError } from '@atlaskit/jql-editor';
import { JastBuilder, JqlAnonymizerVisitor } from '@atlaskit/jql-ast';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { NinChangeboardingTourTarget } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/enable-nin-changeboarding/index.tsx';
import JQLBuilderAdvancedAsync from '@atlassian/jira-jql-builder-advanced/src/async.tsx';
import { useShallowStableReference } from '@atlassian/jira-utils-stable-reference/src/index.tsx';
import JQLBuilderBasicAsync, {
	JQLBuilderBasicFallback,
	JQLBuilderBasicOldAsync as JQLBuilderBasicOld,
} from '@atlassian/jira-jql-builder-basic/src/async.tsx';
import { useSsrPlaceholders } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import JQLBuilderBasicSync from '@atlassian/jira-jql-builder-basic/src/main.tsx';
import {
	useIrremovableFieldsAnalyticsAttributes,
	type IrremovableFields,
} from '@atlassian/jira-jql-builder-basic/src/utils/irremovable-fields/index.tsx';
import type { PreloadedHydrateJqlBuilderQuery } from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/useHydrationQueryLoader.tsx';
import { JQL_BUILDER_PERFORMANCE_MARKS } from '@atlassian/jira-jql-builder-common/src/ui/performance-context/constants.tsx';
import { usePerformanceContext } from '@atlassian/jira-jql-builder-common/src/ui/performance-context/index.tsx';
import { JQLPanelRenderSlotForJQLEditor } from '@atlassian/jira-jql-debugger/src/ui/debugger-panel/index.tsx';
import { JqlDebuggerJqlBuilderContextProvider } from '@atlassian/jira-jql-debugger/src/ui/debugger-panel/context.tsx';
import { useJqlDebuggerStoreActions } from '@atlassian/jira-jql-debugger/src/controllers/jql-debugger-data/index.tsx';
import {
	fireUIAnalytics,
	FireUiAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import UFOLabel from '@atlassian/jira-ufo-label/src/index.tsx';
import { useHydrationQuery } from '@atlassian/jira-jql-builder-common/src/services/hydration-query/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	mapSearchModeToAnalyticAtribute,
	SEARCH_MODE_ADVANCED,
	SEARCH_MODE_BASIC,
	SEARCH_MODE_SWITCHER_LEFT_LOCATION,
} from '../common/constants.tsx';
import type { JQLBuilderBase, OmitJQLBuilderBase, SearchMode } from '../common/types.tsx';
import {
	AdvancedEditorWrapper,
	BeforeJqlBuilderContainer,
	BeforeJqlBuilderListItemContainer,
} from '../common/ui/components/editor-spacing-components/index.tsx';
import { DefaultJqlBuilderContainer } from '../common/ui/default-jql-builder-container/index.tsx';
import { useSelectedSearchMode } from '../controllers/selected-search-mode-state/index.tsx';
import { BasicEditorListContainer, Container } from '../styled.tsx';
import type { JQLBuilderUI$data } from './__generated__/JQLBuilderUI.graphql';
import ExtraControls from './extra-controls/index.tsx';
import { SearchModeSwitcherRenderer } from './search-mode-switcher-renderer/index.tsx';

type JQLBuilderUIImplOldProps = OmitJQLBuilderBase<
	JQLBuilderBase,
	/**
	 * JQLBuilderUIImpl wants the transformed irremovableFields map instead of the raw values that
	 * were passed in from the consumer app so this becomes replaced with `irremovableFields`
	 */
	| 'basicModeVisibleFields'
	// Excluding project key from internal props
	| 'projectKeys'
> & {
	hydrationQueryRef: PreloadedHydrateJqlBuilderQuery;
	isHydrating: boolean;
	/**
	 * User's preferred earch mode prior to running `isQueryTooComplex` which may change it to
	 * advanced mode. For analytics purposes.
	 */
	selectedSearchMode: SearchMode;
	irremovableFields: IrremovableFields;

	/**
	 * Context JQL is the jql used by Graphql queries to return results based on that context
	 * eg: will return fields / issuetypes / statuses specific to a project if that project is in the context jql
	 */
	contextJql: string;
	loadingModeRef: React.MutableRefObject<SearchMode | undefined>;
};

const MaybeSpotlightTarget = componentWithFG(
	'jira_list_nin_merge',
	SpotlightTarget,
	({ children }: PropsWithChildren<{}>) => children,
);

export const JQLBuilderUIImplOld = ({
	analyticsSource,
	query,
	onSearch: onSearchProp,
	onUpdate,
	beforeControls,
	renderExtraControls,
	isSearching,
	onLoad,
	onReady,
	onJourneyStart,
	NoOptionComponent,
	jqlMessages,
	excludedFields,
	searchModes,
	selectedSearchMode,
	hydrationQueryRef,
	isHydrating,
	components,
	hideOperatorDropdownBasicMode,
	irremovableFields,
	hideTextSearchInput,
	searchPlaceholder,
	searchModeSwitcherLocation,
	showOrderByPicker,
	contextJql,
	loadingModeRef,
	...props
}: JQLBuilderUIImplOldProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const displaySsrPlaceholderAttrs = useSsrPlaceholders();

	const jqlDebuggerStoreActions = useJqlDebuggerStoreActions();

	const irremovableFieldsAnalyticsAttributes =
		useIrremovableFieldsAnalyticsAttributes(irremovableFields);

	const { searchMode, setSelectedSearchMode } = useSelectedSearchMode();

	const onPerformanceMark = usePerformanceContext();

	const onSwitchToJql = useCallback(() => {
		setSelectedSearchMode(SEARCH_MODE_ADVANCED, true);
	}, [setSelectedSearchMode]);

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const jqlErrors = (jqlMessages || []).filter((e) => e.type === 'error') as ExternalError[];
		let jqlSanitized;

		if (jqlErrors?.length) {
			const errorAnalyticsEvent = createAnalyticsEvent({});

			try {
				jqlSanitized =
					query && new JastBuilder().build(query).query?.accept(new JqlAnonymizerVisitor());
			} catch (error) {
				jqlSanitized = 'error sanitizing jql';
			}

			fireErrorAnalytics({
				event: errorAnalyticsEvent,
				meta: {
					id: 'jqlBuilderJqlInvalid',
					packageName: 'jiraJqlBuilder',
					teamName: 'empanada',
				},
				attributes: {
					jqlSanitized,
					queryMode: searchMode,
				},
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onJqlBuilderAdvancedReady = useCallback(() => {
		onPerformanceMark({
			mark: JQL_BUILDER_PERFORMANCE_MARKS.JQL_BUILDER_ADVANCED_JS_END,
		});
		onReady && onReady(SEARCH_MODE_ADVANCED);
	}, [onPerformanceMark, onReady]);

	const onJqlBuilderBasicReady = useCallback(() => {
		onReady && onReady(SEARCH_MODE_BASIC);
	}, [onReady]);

	let onSearch: ((jql: string) => void) | undefined = onSearchProp;

	// Keep onSearch undefined if onSearchProp is not provided, as a defined onSearch
	// will alter the behavior of the advanced JQL editor (e.g. rendering search button)
	if (onSearchProp) {
		onSearch = (jql: string) => {
			jqlDebuggerStoreActions?.resetStatus('ON_NEW_JQL_SEARCH', jql);

			onSearchProp(jql);
			const event = createAnalyticsEvent({
				action: 'submitted',
				actionSubject: 'search',
			});
			fireUIAnalytics(event, 'jqlBuilder', { searchMode });
		};
	}

	const { SearchModeSwitcher, EditorContainer: CustomEditorContainer } = components ?? {};

	const isSearchModeToggleEnabled = searchModes ? searchModes.length > 1 : true;

	const isSearchModeSwitcherOnLeft =
		searchModeSwitcherLocation === SEARCH_MODE_SWITCHER_LEFT_LOCATION;

	const SearchModeSwitcherComponent = useMemo(
		() => (
			<SearchModeSwitcherRenderer
				isSearchModeToggleEnabled={isSearchModeToggleEnabled}
				SearchModeSwitcher={SearchModeSwitcher}
				isHydrating={isHydrating}
				isSearchModeSwitcherOnLeft={isSearchModeSwitcherOnLeft}
			/>
		),
		[SearchModeSwitcher, isHydrating, isSearchModeSwitcherOnLeft, isSearchModeToggleEnabled],
	);

	// We need to memoize props before passing it into useMemo because an object splat is a new reference on every render
	const editorForwardedProps = useShallowStableReference(props);
	const Editor = useMemo(() => {
		let jqlBuilderComponent: JSX.Element;
		switch (searchMode) {
			case SEARCH_MODE_BASIC:
				jqlBuilderComponent = (
					<UFOLabel name="jql-builder-basic">
						<JQLBuilderBasicOld
							query={query}
							// consumers can hide search feature from advanced jql editor by not passing onSearch Handler
							// if no onSearch handler is passed, Basic JQL editor would fallback to onUpdate handler
							onSearch={onSearch || onUpdate || noop}
							onSwitchToJql={onSwitchToJql}
							excludedFields={excludedFields}
							onReady={onJqlBuilderBasicReady}
							onJourneyStart={onJourneyStart}
							NoOptionComponent={NoOptionComponent}
							hydrationQueryRef={hydrationQueryRef}
							isHydrating={isHydrating}
							shouldUseListMarkup
							hideOperatorDropdown={hideOperatorDropdownBasicMode}
							irremovableFields={irremovableFields}
							hideTextSearchInput={hideTextSearchInput}
							searchPlaceholder={searchPlaceholder}
							showOrderByPicker={showOrderByPicker}
							contextJql={contextJql}
							{...editorForwardedProps}
						/>
					</UFOLabel>
				);
				break;
			default: {
				jqlBuilderComponent = (
					<UFOLabel name="jql-builder-advanced">
						{/* Remove all the NinChangeboardingTourTarget on clean up of 'remove_nin_changeboarding_rll_usages', should be showing as empty fragments already */}
						<NinChangeboardingTourTarget engagementId="nin.jql-builder.advanced">
							<AdvancedEditorWrapper>
								<JqlDebuggerJqlBuilderContextProvider
									jqlMessages={jqlMessages}
									query={query}
									onSearch={onSearch}
								>
									<JQLBuilderAdvancedAsync
										analyticsSource={analyticsSource}
										onSearch={onSearch}
										onUpdate={onUpdate}
										query={query}
										jqlMessages={jqlMessages}
										isSearching={isSearching}
										isCompact
										excludedAutocompleteFields={excludedFields}
										onReady={onJqlBuilderAdvancedReady}
										onJourneyStart={onJourneyStart}
										customComponents={{ ErrorMessage: JQLPanelRenderSlotForJQLEditor }}
										{...editorForwardedProps}
									/>
								</JqlDebuggerJqlBuilderContextProvider>
							</AdvancedEditorWrapper>
						</NinChangeboardingTourTarget>
					</UFOLabel>
				);
			}
		}

		const BeforeJqlBuilderWrapper =
			searchMode === SEARCH_MODE_BASIC
				? BeforeJqlBuilderListItemContainer
				: BeforeJqlBuilderContainer;

		const showSearchModeSwitcher = isSearchModeToggleEnabled && isSearchModeSwitcherOnLeft;

		return (
			<>
				{beforeControls && <BeforeJqlBuilderWrapper>{beforeControls}</BeforeJqlBuilderWrapper>}
				{showSearchModeSwitcher && (
					<BeforeJqlBuilderWrapper>{SearchModeSwitcherComponent}</BeforeJqlBuilderWrapper>
				)}
				{jqlBuilderComponent}
				{renderExtraControls && (
					<ExtraControls
						searchMode={searchMode}
						renderExtraControls={renderExtraControls}
						shouldUseListMarkup={searchMode === SEARCH_MODE_BASIC}
					/>
				)}
			</>
		);
	}, [
		searchMode,
		beforeControls,
		isSearchModeToggleEnabled,
		isSearchModeSwitcherOnLeft,
		SearchModeSwitcherComponent,
		renderExtraControls,
		query,
		onSearch,
		onUpdate,
		onSwitchToJql,
		excludedFields,
		onJqlBuilderBasicReady,
		onJourneyStart,
		NoOptionComponent,
		hydrationQueryRef,
		isHydrating,
		hideOperatorDropdownBasicMode,
		irremovableFields,
		hideTextSearchInput,
		searchPlaceholder,
		showOrderByPicker,
		contextJql,
		editorForwardedProps,
		analyticsSource,
		jqlMessages,
		isSearching,
		onJqlBuilderAdvancedReady,
	]);

	const EffectiveJqlBuilderContainer = CustomEditorContainer ?? DefaultJqlBuilderContainer;

	return (
		<Container
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			data-testid="jql-builder"
			data-vc="jql-builder-ui-container"
			data-ssr-placeholder={
				__SERVER__ &&
				displaySsrPlaceholderAttrs &&
				!fg('jfp-magma-nin-hydration-fixes') &&
				fg('queues_jql_builder_hydration_query')
					? 'jql-builder-ui-container'
					: undefined
			}
			data-ssr-placeholder-replace={
				__SERVER__ ||
				displaySsrPlaceholderAttrs ||
				fg('jfp-magma-nin-hydration-fixes') ||
				!fg('queues_jql_builder_hydration_query')
					? undefined
					: 'jql-builder-ui-container'
			}
		>
			<MaybeSpotlightTarget name="jql-builder.ui.jql-builder.editor">
				<EffectiveJqlBuilderContainer
					showAdvancedEditor={searchMode === SEARCH_MODE_ADVANCED}
					data-testid="jql-builder.ui.effective-jql-builder-container"
				>
					{searchMode === SEARCH_MODE_BASIC ? (
						<BasicEditorListContainer data-vc="jql-builder-basic-editor-container">
							{Editor}
						</BasicEditorListContainer>
					) : (
						Editor
					)}
				</EffectiveJqlBuilderContainer>
			</MaybeSpotlightTarget>
			{!isSearchModeSwitcherOnLeft && SearchModeSwitcherComponent}
			<FireUiAnalytics
				actionSubject="screen"
				action="viewed"
				actionSubjectId="jqlBuilder"
				attributes={{
					selectedSearchMode,
					queryMode: mapSearchModeToAnalyticAtribute[searchMode],
					...irremovableFieldsAnalyticsAttributes,
					loadingMode: loadingModeRef.current,
				}}
			/>
		</Container>
	);
};

type JQLBuilderUIImplProps = OmitJQLBuilderBase<
	JQLBuilderBase,
	/**
	 * JQLBuilderUIImpl wants the transformed irremovableFields map instead of the raw values that
	 * were passed in from the consumer app so this becomes replaced with `irremovableFields`
	 */
	| 'basicModeVisibleFields'
	// Excluding project key from internal props
	| 'projectKeys'
> & {
	hydrationData: JQLBuilderUI$data;
	/**
	 * User's preferred earch mode prior to running `isQueryTooComplex` which may change it to
	 * advanced mode. For analytics purposes.
	 */
	selectedSearchMode: SearchMode;
	irremovableFields: IrremovableFields;

	/**
	 * Context JQL is the jql used by Graphql queries to return results based on that context
	 * eg: will return fields / issuetypes / statuses specific to a project if that project is in the context jql
	 */
	contextJql: string;
	loadingModeRef: React.MutableRefObject<SearchMode | undefined>;
};

export const JQLBuilderBasic = componentWithFG(
	'render_jql_builder_in_ssr',
	JQLBuilderBasicSync,
	JQLBuilderBasicAsync,
);

export const JQLBuilderUIImpl = ({
	analyticsSource,
	query,
	onSearch: onSearchProp,
	onUpdate,
	beforeControls,
	renderExtraControls,
	isSearching,
	onLoad,
	onReady,
	onJourneyStart,
	NoOptionComponent,
	jqlMessages,
	excludedFields,
	searchModes,
	selectedSearchMode,
	hydrationData,
	components,
	hideOperatorDropdownBasicMode,
	irremovableFields,
	hideTextSearchInput,
	searchPlaceholder,
	searchModeSwitcherLocation,
	showOrderByPicker,
	contextJql,
	loadingModeRef,
	...props
}: JQLBuilderUIImplProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const displaySsrPlaceholderAttrs = useSsrPlaceholders();

	const jqlDebuggerStoreActions = useJqlDebuggerStoreActions();

	const irremovableFieldsAnalyticsAttributes =
		useIrremovableFieldsAnalyticsAttributes(irremovableFields);

	const { searchMode, setSelectedSearchMode } = useSelectedSearchMode();
	const { hydrationQueryIsFetching } = useHydrationQuery();

	const onPerformanceMark = usePerformanceContext();

	const onSwitchToJql = useCallback(() => {
		setSelectedSearchMode(SEARCH_MODE_ADVANCED, true);
	}, [setSelectedSearchMode]);

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const jqlErrors = (jqlMessages || []).filter((e) => e.type === 'error') as ExternalError[];
		let jqlSanitized;
		if (jqlErrors?.length) {
			try {
				jqlSanitized =
					query && new JastBuilder().build(query).query?.accept(new JqlAnonymizerVisitor());
			} catch (error) {
				jqlSanitized = 'error sanitizing jql';
			}
			const errorAnalyticsEvent = createAnalyticsEvent({});

			fireErrorAnalytics({
				event: errorAnalyticsEvent,
				meta: {
					id: 'jqlBuilderJqlInvalid',
					packageName: 'jiraJqlBuilder',
					teamName: 'empanada',
				},
				attributes: {
					jqlSanitized,
					queryMode: searchMode,
				},
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onJqlBuilderAdvancedReady = useCallback(() => {
		onPerformanceMark({
			mark: JQL_BUILDER_PERFORMANCE_MARKS.JQL_BUILDER_ADVANCED_JS_END,
		});
		onReady && onReady(SEARCH_MODE_ADVANCED);
	}, [onPerformanceMark, onReady]);

	const onJqlBuilderBasicLoad = useCallback(() => {
		onLoad && onLoad(SEARCH_MODE_BASIC);
	}, [onLoad]);

	const onJqlBuilderBasicReady = useCallback(() => {
		onReady && onReady(SEARCH_MODE_BASIC);
	}, [onReady]);

	let onSearch: ((jql: string) => void) | undefined = onSearchProp;

	// Keep onSearch undefined if onSearchProp is not provided, as a defined onSearch
	// will alter the behavior of the advanced JQL editor (e.g. rendering search button)
	if (onSearchProp) {
		onSearch = (jql: string) => {
			jqlDebuggerStoreActions?.resetStatus('ON_NEW_JQL_SEARCH', jql);

			onSearchProp(jql);
			const event = createAnalyticsEvent({
				action: 'submitted',
				actionSubject: 'search',
			});
			fireUIAnalytics(event, 'jqlBuilder', { searchMode });
		};
	}

	const { SearchModeSwitcher, EditorContainer: CustomEditorContainer } = components ?? {};

	const isSearchModeToggleEnabled = searchModes ? searchModes.length > 1 : true;

	const isSearchModeSwitcherOnLeft =
		searchModeSwitcherLocation === SEARCH_MODE_SWITCHER_LEFT_LOCATION;

	const SearchModeSwitcherComponent = useMemo(
		() => (
			<SearchModeSwitcherRenderer
				isSearchModeToggleEnabled={isSearchModeToggleEnabled}
				SearchModeSwitcher={SearchModeSwitcher}
				isHydrating={hydrationQueryIsFetching}
				isSearchModeSwitcherOnLeft={isSearchModeSwitcherOnLeft}
			/>
		),
		[
			SearchModeSwitcher,
			hydrationQueryIsFetching,
			isSearchModeSwitcherOnLeft,
			isSearchModeToggleEnabled,
		],
	);

	// We need to memoize props before passing it into useMemo because an object splat is a new reference on every render
	const editorForwardedProps = useShallowStableReference(props);
	const Editor = useMemo(() => {
		let jqlBuilderComponent: JSX.Element;
		switch (searchMode) {
			case SEARCH_MODE_BASIC: {
				const basicBuilder = (
					<JQLBuilderBasic
						query={query}
						// consumers can hide search feature from advanced jql editor by not passing onSearch Handler
						// if no onSearch handler is passed, Basic JQL editor would fallback to onUpdate handler
						onSearch={onSearch || onUpdate || noop}
						onSwitchToJql={onSwitchToJql}
						excludedFields={excludedFields}
						onReady={onJqlBuilderBasicReady}
						onJourneyStart={onJourneyStart}
						NoOptionComponent={NoOptionComponent}
						AddFilterButtonComponent={components?.AddFilterButtonComponent}
						hydrationData={hydrationData}
						isHydrating={hydrationQueryIsFetching}
						shouldUseListMarkup
						hideOperatorDropdown={hideOperatorDropdownBasicMode}
						irremovableFields={irremovableFields}
						hideTextSearchInput={hideTextSearchInput}
						searchPlaceholder={searchPlaceholder}
						showOrderByPicker={showOrderByPicker}
						contextJql={contextJql}
						{...editorForwardedProps}
					/>
				);
				// In SSR it is possible for the JQL Builder Basic component to timeout during render.
				// We would like to suspend at this level with a Placeholder component and an appropriate fallback.
				const basicBuilderWithPlaceholder = (
					<Placeholder
						name="jql-builder-basic-fallback"
						fallback={
							<JQLBuilderBasicFallback
								query={query}
								excludedFields={excludedFields}
								onLoad={onJqlBuilderBasicLoad}
								shouldUseListMarkup
								irremovableFields={irremovableFields}
								hideTextSearchInput={hideTextSearchInput}
								searchPlaceholder={searchPlaceholder}
								showOrderByPicker={showOrderByPicker}
							/>
						}
					>
						{basicBuilder}
					</Placeholder>
				);

				if (
					fg('jfp-magma-nin-hydration-fixes') ||
					(__SERVER__ && fg('render_jql_builder_in_ssr'))
				) {
					jqlBuilderComponent = (
						<UFOLabel name="jql-builder-basic">{basicBuilderWithPlaceholder}</UFOLabel>
					);
				} else {
					jqlBuilderComponent = <UFOLabel name="jql-builder-basic">{basicBuilder}</UFOLabel>;
				}
				break;
			}
			default: {
				jqlBuilderComponent = (
					<UFOLabel name="jql-builder-advanced">
						{/* Remove all the NinChangeboardingTourTarget on clean up of 'remove_nin_changeboarding_rll_usages', should be showing as empty fragments already */}
						<NinChangeboardingTourTarget engagementId="nin.jql-builder.advanced">
							<AdvancedEditorWrapper>
								<JqlDebuggerJqlBuilderContextProvider
									jqlMessages={jqlMessages}
									query={query}
									onSearch={onSearch}
								>
									<JQLBuilderAdvancedAsync
										analyticsSource={analyticsSource}
										onSearch={onSearch}
										onUpdate={onUpdate}
										query={query}
										jqlMessages={jqlMessages}
										isSearching={isSearching}
										isCompact
										excludedAutocompleteFields={excludedFields}
										onReady={onJqlBuilderAdvancedReady}
										onJourneyStart={onJourneyStart}
										customComponents={{ ErrorMessage: JQLPanelRenderSlotForJQLEditor }}
										{...editorForwardedProps}
									/>
								</JqlDebuggerJqlBuilderContextProvider>
							</AdvancedEditorWrapper>
						</NinChangeboardingTourTarget>
					</UFOLabel>
				);
			}
		}

		const BeforeJqlBuilderWrapper =
			searchMode === SEARCH_MODE_BASIC
				? BeforeJqlBuilderListItemContainer
				: BeforeJqlBuilderContainer;

		const showSearchModeSwitcher = isSearchModeToggleEnabled && isSearchModeSwitcherOnLeft;

		return (
			<>
				{beforeControls && <BeforeJqlBuilderWrapper>{beforeControls}</BeforeJqlBuilderWrapper>}
				{showSearchModeSwitcher && (
					<BeforeJqlBuilderWrapper>{SearchModeSwitcherComponent}</BeforeJqlBuilderWrapper>
				)}
				{jqlBuilderComponent}
				{renderExtraControls && (
					<ExtraControls
						searchMode={searchMode}
						renderExtraControls={renderExtraControls}
						shouldUseListMarkup={searchMode === SEARCH_MODE_BASIC}
					/>
				)}
			</>
		);
	}, [
		searchMode,
		isSearchModeToggleEnabled,
		isSearchModeSwitcherOnLeft,
		beforeControls,
		SearchModeSwitcherComponent,
		renderExtraControls,
		query,
		onSearch,
		onUpdate,
		onSwitchToJql,
		excludedFields,
		onJqlBuilderBasicLoad,
		onJqlBuilderBasicReady,
		onJourneyStart,
		NoOptionComponent,
		components?.AddFilterButtonComponent,
		hydrationData,
		hydrationQueryIsFetching,
		hideOperatorDropdownBasicMode,
		irremovableFields,
		hideTextSearchInput,
		searchPlaceholder,
		showOrderByPicker,
		contextJql,
		editorForwardedProps,
		jqlMessages,
		analyticsSource,
		isSearching,
		onJqlBuilderAdvancedReady,
	]);

	const EffectiveJqlBuilderContainer = CustomEditorContainer ?? DefaultJqlBuilderContainer;

	return (
		<Container
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			data-testid="jql-builder"
			data-vc="jql-builder-ui-container"
			data-ssr-placeholder={
				__SERVER__ &&
				displaySsrPlaceholderAttrs &&
				!fg('jfp-magma-nin-hydration-fixes') &&
				fg('queues_jql_builder_hydration_query')
					? 'jql-builder-ui-container'
					: undefined
			}
			data-ssr-placeholder-replace={
				__SERVER__ ||
				!displaySsrPlaceholderAttrs ||
				fg('jfp-magma-nin-hydration-fixes') ||
				!fg('queues_jql_builder_hydration_query')
					? undefined
					: 'jql-builder-ui-container'
			}
		>
			<MaybeSpotlightTarget name="jql-builder.ui.jql-builder.editor">
				<EffectiveJqlBuilderContainer
					showAdvancedEditor={searchMode === SEARCH_MODE_ADVANCED}
					data-testid="jql-builder.ui.effective-jql-builder-container"
				>
					{searchMode === SEARCH_MODE_BASIC ? (
						<BasicEditorListContainer data-vc="jql-builder-basic-editor-container">
							{Editor}
						</BasicEditorListContainer>
					) : (
						Editor
					)}
				</EffectiveJqlBuilderContainer>
			</MaybeSpotlightTarget>
			{!isSearchModeSwitcherOnLeft && SearchModeSwitcherComponent}
			<FireUiAnalytics
				actionSubject="screen"
				action="viewed"
				actionSubjectId="jqlBuilder"
				attributes={{
					selectedSearchMode,
					queryMode: mapSearchModeToAnalyticAtribute[searchMode],
					...irremovableFieldsAnalyticsAttributes,
					loadingMode: loadingModeRef.current,
				}}
			/>
		</Container>
	);
};
